import { useEffect, useRef, useState } from "react";
import "./CustomCursor.css";

const interactiveSelector = [
  "a",
  "button",
  ".project-card",
  ".tech-stack-group",
  ".certificate-image",
  ".tilted-card-figure",
  ".portfolio-navbar",
  ".portfolio-nav-links a",
  "[role='button']",
].join(",");

const textSelector = [
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "span",
  "strong",
  "small",
  ".about-text",
].join(",");

function CustomCursor() {
  const cursorRef = useRef(null);
  const followerRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const cursor = useRef({ x: 0, y: 0 });
  const follower = useRef({ x: 0, y: 0 });
  const activeTarget = useRef(null);
  const frameRef = useRef(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [variant, setVariant] = useState("default");
  const [isClicking, setIsClicking] = useState(false);
  const [ripples, setRipples] = useState([]);

  useEffect(() => {
    const finePointer = window.matchMedia("(pointer: fine)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateEnabled = () => setIsEnabled(finePointer.matches && !reducedMotion.matches);

    updateEnabled();
    finePointer.addEventListener("change", updateEnabled);
    reducedMotion.addEventListener("change", updateEnabled);

    return () => {
      finePointer.removeEventListener("change", updateEnabled);
      reducedMotion.removeEventListener("change", updateEnabled);
    };
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      document.body.classList.remove("custom-cursor-enabled");
      return undefined;
    }

    document.body.classList.add("custom-cursor-enabled");

    const handleMouseMove = (event) => {
      mouse.current.x = event.clientX;
      mouse.current.y = event.clientY;
    };

    const handleMouseOver = (event) => {
      const interactive = event.target.closest(interactiveSelector);
      const text = event.target.closest(textSelector);

      if (interactive) {
        activeTarget.current = interactive;
        setVariant("interactive");
      } else if (text) {
        activeTarget.current = null;
        setVariant("text");
      } else {
        activeTarget.current = null;
        setVariant("default");
      }
    };

    const handleMouseDown = () => {
      setIsClicking(true);
      const id = Date.now();
      setRipples((items) => [...items.slice(-3), { id, x: mouse.current.x, y: mouse.current.y }]);
      window.setTimeout(() => {
        setRipples((items) => items.filter((item) => item.id !== id));
      }, 520);
    };

    const handleMouseUp = () => setIsClicking(false);

    const animate = () => {
      let targetX = mouse.current.x;
      let targetY = mouse.current.y;

      if (activeTarget.current) {
        const rect = activeTarget.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        targetX += (centerX - targetX) * 0.14;
        targetY += (centerY - targetY) * 0.14;
      }

      cursor.current.x += (targetX - cursor.current.x) * 0.34;
      cursor.current.y += (targetY - cursor.current.y) * 0.34;
      follower.current.x += (targetX - follower.current.x) * 0.18;
      follower.current.y += (targetY - follower.current.y) * 0.18;

      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate3d(${cursor.current.x}px, ${cursor.current.y}px, 0) translate(-50%, -50%)`;
      }

      if (followerRef.current) {
        followerRef.current.style.transform = `translate3d(${follower.current.x}px, ${follower.current.y}px, 0) translate(-50%, -50%)`;
      }

      frameRef.current = requestAnimationFrame(animate);
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("mouseover", handleMouseOver, { passive: true });
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);
    frameRef.current = requestAnimationFrame(animate);

    return () => {
      document.body.classList.remove("custom-cursor-enabled");
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseover", handleMouseOver);
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
      cancelAnimationFrame(frameRef.current);
    };
  }, [isEnabled]);

  if (!isEnabled) return null;

  return (
    <>
      <div
        ref={followerRef}
        className={`custom-cursor-follower custom-cursor-${variant}${isClicking ? " is-clicking" : ""}`}
        aria-hidden="true"
      />
      <div
        ref={cursorRef}
        className={`custom-cursor-dot custom-cursor-${variant}${isClicking ? " is-clicking" : ""}`}
        aria-hidden="true"
      />
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="custom-cursor-ripple"
          style={{ left: ripple.x, top: ripple.y }}
          aria-hidden="true"
        />
      ))}
    </>
  );
}

export default CustomCursor;
